import PropTypes from "prop-types";
import Button from "components/Button";
import StatusBadge from "components/common/StatusBadge";
import AppModal from "components/common/AppModal";
import {
  normalizeUserStatus,
  USER_STATUS_OPTIONS,
} from "utils/usersList";

export default function UserChangeStatusModal({
  open,
  user,
  statusDraft,
  updating,
  onStatusChange,
  onClose,
  onUpdate,
}) {
  if (!user) return null;

  const currentStatus = normalizeUserStatus(user.status, "inactive");
  const statusUnchanged = statusDraft === currentStatus;

  return (
    <AppModal
      open={open}
      title="Change user status"
      onClose={onClose}
      preventClose={updating}
      maxWidth="md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={updating}>
            Cancel
          </Button>
          <Button type="button" onClick={onUpdate} disabled={updating || statusUnchanged}>
            {updating ? "Updating…" : "Update"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-500">
            Current status
          </p>
          <div className="mt-1.5">
            <StatusBadge status={user.status} as="span" />
          </div>
          <p className="mt-2 truncate text-sm font-medium text-text-900">{user.full_name}</p>
          {user.email ? (
            <p className="truncate text-xs text-text-500">{user.email}</p>
          ) : null}
        </div>

        <div>
          <label htmlFor="user-status-select" className="mb-1 block text-xs font-semibold text-text-500">
            New status
          </label>
          <select
            id="user-status-select"
            value={statusDraft}
            onChange={(event) => {
              const next = event.target.value;
              if (next !== currentStatus) onStatusChange(next);
            }}
            disabled={updating}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-text-900 outline-none ring-brand-600 focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {USER_STATUS_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value} disabled={value === currentStatus}>
                {label}
                {value === currentStatus ? " (current)" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>
    </AppModal>
  );
}

UserChangeStatusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    full_name: PropTypes.string,
    email: PropTypes.string,
    status: PropTypes.string,
  }),
  statusDraft: PropTypes.string.isRequired,
  updating: PropTypes.bool.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};
